import React, { useState } from 'react';
import StoreNavigation from '../components/StoreNavigation';
import StoreFooter from '../components/StoreFooter';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import { Star, Gift, Users, Megaphone, Heart, ArrowRight, Instagram, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Ambassador = () => {
  const { getCartItemCount } = useCart();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    instagram: '',
    location: '',
    trade: '',
    experience: '',
    why: ''
  });

  const perks = [
    {
      icon: Gift,
      title: 'Free Gear',
      description: 'Receive Vesta gear to wear and showcase in your workshop'
    },
    {
      icon: Megaphone,
      title: 'Be Featured',
      description: 'Get spotlighted on our social media and website'
    },
    {
      icon: Star,
      title: 'Early Access',
      description: 'Be first to try new products and have input on future designs'
    },
    {
      icon: Users,
      title: 'Community',
      description: 'Connect with other women in trades across Australia'
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact/ambassador`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast({
          title: "Application Submitted!",
          description: "We'll review your application and reach out within a week.",
        });
        setFormData({
          name: '',
          email: '',
          instagram: '',
          location: '',
          trade: '',
          experience: '',
          why: ''
        });
      } else {
        throw new Error(data.message || 'Failed to submit application');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit application. Please try again or email us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen bg-white">
      <StoreNavigation cartItemCount={getCartItemCount()} />

      {/* Hero */}
      <section className="pt-32 pb-24 bg-gradient-to-br from-[#004D51] to-[#003338] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'url(/automotive-2.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6">
              <div className="px-6 py-2 bg-[#DB5A14] text-white font-black text-sm tracking-widest">
                JOIN THE CREW
              </div>
            </div>
            <h1 className="text-5xl md:text-7xl font-black mb-6">
              Vesta <span className="text-[#F39C49]">Ambassador</span> Program
            </h1>
            <p className="text-xl text-[#CFD2D4] max-w-2xl mx-auto">
              Are you a woman working in automotive refinishing who's passionate about 
              representing quality workwear? We want you on our team.
            </p>
          </div>
        </div>
      </section>

      {/* Perks */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-black text-[#004D51] mb-12 text-center">
            Ambassador Perks
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {perks.map((perk, index) => (
              <div 
                key={index}
                className="bg-white border-4 border-[#004D51] rounded-xl p-6 text-center hover:shadow-xl transition-all transform hover:-translate-y-1"
              >
                <div className="w-14 h-14 bg-[#DB5A14] rounded-full flex items-center justify-center mx-auto mb-4">
                  <perk.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-black text-[#004D51] mb-3">{perk.title}</h3>
                <p className="text-[#545759]">{perk.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We're Looking For */}
      <section className="py-24 bg-[#F3CEB2]/20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-black text-[#004D51] mb-8 text-center">
              Who We're Looking For
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8 border-4 border-[#F39C49]">
                <Heart className="w-10 h-10 text-[#DB5A14] mb-4" />
                <h3 className="text-2xl font-black text-[#004D51] mb-4">You're Passionate</h3>
                <p className="text-[#545759]">
                  You love what you do and you're proud to be a woman in trades. 
                  You want to inspire others to join the industry.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8 border-4 border-[#F39C49]">
                <Instagram className="w-10 h-10 text-[#DB5A14] mb-4" />
                <h3 className="text-2xl font-black text-[#004D51] mb-4">You're Active Online</h3>
                <p className="text-[#545759]">
                  You enjoy sharing your work on social media and engaging with 
                  the trades community. No follower minimum required!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-4xl font-black text-[#004D51] mb-4 text-center">
              Apply to Be an Ambassador
            </h2>
            <p className="text-[#545759] text-center mb-12">
              Tell us about yourself and why you'd be a great Vesta Ambassador.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-black text-[#004D51] mb-2">
                    Your Name *
                  </label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="h-12 border-2 border-[#004D51] focus:border-[#DB5A14]"
                    placeholder="Your Name"
                    data-testid="ambassador-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-black text-[#004D51] mb-2">
                    Email *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="h-12 border-2 border-[#004D51] focus:border-[#DB5A14]"
                    placeholder="your@email.com"
                    data-testid="ambassador-email"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-black text-[#004D51] mb-2">
                    Instagram Handle
                  </label>
                  <Input
                    name="instagram"
                    value={formData.instagram}
                    onChange={handleChange}
                    className="h-12 border-2 border-[#004D51] focus:border-[#DB5A14]"
                    placeholder="@yourhandle"
                    data-testid="ambassador-instagram"
                  />
                </div>
                <div>
                  <label className="block text-sm font-black text-[#004D51] mb-2">
                    Location (State) *
                  </label>
                  <select
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    className="w-full h-12 px-4 border-2 border-[#004D51] rounded-md focus:border-[#DB5A14] focus:outline-none bg-white"
                    data-testid="ambassador-location"
                  >
                    <option value="">Select state...</option>
                    <option value="NSW">New South Wales</option>
                    <option value="VIC">Victoria</option>
                    <option value="QLD">Queensland</option>
                    <option value="WA">Western Australia</option>
                    <option value="SA">South Australia</option>
                    <option value="TAS">Tasmania</option>
                    <option value="NT">Northern Territory</option>
                    <option value="ACT">ACT</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-black text-[#004D51] mb-2">
                    Your Trade *
                  </label>
                  <Input
                    name="trade"
                    value={formData.trade}
                    onChange={handleChange}
                    required
                    className="h-12 border-2 border-[#004D51] focus:border-[#DB5A14]"
                    placeholder="e.g., Spray Painter, Panel Beater"
                    data-testid="ambassador-trade"
                  />
                </div>
                <div>
                  <label className="block text-sm font-black text-[#004D51] mb-2">
                    Years of Experience
                  </label>
                  <select
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    className="w-full h-12 px-4 border-2 border-[#004D51] rounded-md focus:border-[#DB5A14] focus:outline-none bg-white"
                    data-testid="ambassador-experience"
                  >
                    <option value="">Select...</option>
                    <option value="apprentice">Currently an Apprentice</option>
                    <option value="1-3">1-3 years</option>
                    <option value="3-5">3-5 years</option>
                    <option value="5-10">5-10 years</option>
                    <option value="10+">10+ years</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-black text-[#004D51] mb-2">
                  Why Do You Want to Be a Vesta Ambassador? *
                </label>
                <Textarea
                  name="why"
                  value={formData.why}
                  onChange={handleChange}
                  required
                  className="min-h-32 border-2 border-[#004D51] focus:border-[#DB5A14]"
                  placeholder="Tell us your story. What does being a woman in trades mean to you?"
                  data-testid="ambassador-why"
                />
              </div>

              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 bg-[#DB5A14] hover:bg-[#F39C49] text-white font-black text-lg disabled:opacity-70"
                data-testid="ambassador-submit"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Application <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-24 bg-[#004D51] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <blockquote className="text-3xl font-black italic mb-6">
              "Vesta isn't just workwear—it's a movement for women in trades. 
              Join us in changing the game."
            </blockquote>
            <p className="text-[#F39C49] font-bold">— The Vesta Team</p>
          </div>
        </div>
      </section>

      <StoreFooter />
    </div>
  );
};

export default Ambassador;
